import { connect } from "react-redux";
import { Card, Button, Form, Input, Badge, Upload, message } from "antd";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { usePreviousState } from "../../../utils/customHooks";
import { addTranslations } from "../../../utils/functions/javaScriptHelper";
import {
	updateCompanyBasicInformation,
	updateCompanyLogo,
} from "../../../actions/companyActions";
import { defaultCompanyLogoURL } from "../../../utils/constants";
import ImageUploadCameraIcon from "../../common/icons/imageUploadCamera";
import ImgCrop from "antd-img-crop";
import GalkButton from "../../common/component/button";
import { EditPenOnPaperRightHandIcon, RefreshIcon } from "../../common/icons";
import "./basicInformation.css";

const getSrcFromFile = (file) => {
	return new Promise((resolve) => {
		const reader = new FileReader();
		reader.readAsDataURL(file);
		reader.onloadend = () => resolve(reader.result);
	});
};

const BasicInformation = ({
	companyBasicDetails,
	isActionProgress,
	updateCompanyBasicInformation,
	updateCompanyLogo,
	setCompletionBool,
}) => {
	const [form] = Form.useForm();
	const prevActionInProgressValue = usePreviousState(isActionProgress);
	const [isEditable, setIsEditable] = useState(false);
	const [logo, setLogo] = useState(defaultCompanyLogoURL);
	const [logoObject, setLogoObject] = useState(null);
	const { t } = useTranslation();

	useEffect(() => {
		if (!isActionProgress && prevActionInProgressValue) {
			setIsEditable(false);
		}
	}, [isActionProgress]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		setLogo(companyBasicDetails.logo);
	}, [companyBasicDetails.logo]);

	const resetAll = () => {
		form.resetFields();
		setIsEditable(false);
		setLogoObject(null);
	};

	const updateInformation = async (values) => {
		let updatedValue = {
			name: values.name || "",
			nameInEnglish: values.nameInEnglish || "",
			industry: values.industry || "",
			email: values.email,
			address: values.address || "",
			size: values.size || "",
			website: values.website || "",
		};
		let updatedValueWithTranslations = {
			...(await addTranslations(updatedValue, ["industry"])),
		};

		updateCompanyBasicInformation(updatedValueWithTranslations);
		updateLogo();
		setCompletionBool("basicInfo");
	};

	const fetchFileData = async (file) => {
		if (file) {
			// const file = info.file.originFileObj;

			const isAcceptableFileType =
				file.type === "image/jpeg" ||
				file.type === "image/svg+xml" ||
				file.type === "image/gif" ||
				file.type === "image/png";

			if (!isAcceptableFileType) {
				message.error("You can only upload jpeg/png/svg/gif file!");
			}

			const isLt10M = file.size / 1024 / 1024 < 10;

			if (!isLt10M) {
				message.error("Image must be smaller than 10MB!");
			}

			if (isAcceptableFileType && isLt10M) {
				const url = await getSrcFromFile(file);
				setLogo(url);
				setLogoObject(file);
			}
		} else {
			setLogoObject(null);
		}

		return false;
	};

	const handleLogoUploadChange = ({ fileList: newFileList }) => {
		// if (newFileList.length > 0) {
		// 	let file = newFileList[0].originFileObj;
		// 	const isAcceptableFileType =
		// 		file.type === "image/jpeg" ||
		// 		file.type === "image/svg+xml" ||
		// 		file.type === "image/gif" ||
		// 		file.type === "image/png";
		// 	if (!isAcceptableFileType) {
		// 		message.error("You can only upload jpeg/png/svg/gif file!");
		// 	}
		// 	const isLt10M = file.size / 1024 / 1024 < 10;
		// 	if (!isLt10M) {
		// 		message.error("Image must be smaller than 10MB!");
		// 	}
		// 	if (isAcceptableFileType && isLt10M) {
		// 		let reader = new FileReader();
		// 		reader.onloadend = () => {
		// 			setLogoObject(file);
		// 			setLogo(reader.result);
		// 		};
		// 		reader.readAsDataURL(file);
		// 	}
		// } else {
		// 	setLogoObject(null);
		// }
	};

	const updateLogo = () => {
		if (logoObject) {
			updateCompanyLogo(logoObject);
			setCompletionBool("corporateLogo");
		} else {
			message.error("Please select a new logo and then click on save.");
		}
	};

	return (
		<Card
			type="inner"
			title={`${t("basic_information")}`}
			extra={
				isEditable ? (
					<div style={{ display: "flex", gap: "16px" }}>
						<GalkButton
							type="secondary"
							height={36}
							additionalStyle={{ padding: "13px 16px" }}
							onClick={resetAll}
							disabled={isActionProgress}
							style={{ marginRight: 10 }}
						>
							{t("editProject_modalAction_cancel")}
						</GalkButton>
						<GalkButton
							type="primary"
							height={36}
							additionalStyle={{ padding: "13px 16px" }}
							onClick={() => form.submit()}
							disabled={false}
							loading={isActionProgress}
						>
							<RefreshIcon width={14} height={14} />
							{t("editProject_modalAction_submit")}
						</GalkButton>
					</div>
				) : (
					<GalkButton
						type="default"
						height={36}
						additionalStyle={{ padding: "13px 16px" }}
						onClick={() => setIsEditable(true)}
					>
						<EditPenOnPaperRightHandIcon width={14} height={14} />
						{t("edit")}
					</GalkButton>
				)
			}
		>
			<div className="basicInfo_body_container">
				<div className="basicInfo_body_companyLogo_container">
					<div className="basicInfo_body_companyLogo_uploadControl">
						{isEditable ? (
							<ImgCrop rotate>
								<Upload
									name="avatar"
									// listType="picture-circle"
									// className="avatar-uploader"
									showUploadList={false}
									beforeUpload={fetchFileData}
									onChange={handleLogoUploadChange}
									maxCount={1}
									disabled={!isEditable}
								>
									<img
										className="editableImage"
										src={logo || defaultCompanyLogoURL}
										alt="avatar"
									/>
									<div className="uploadText">
										<ImageUploadCameraIcon height={54} width={54} />
									</div>
								</Upload>
							</ImgCrop>
						) : (
							<img
								className="notEditableImage"
								src={logo || defaultCompanyLogoURL}
								alt="avatar"
							/>
						)}
					</div>
					{isEditable && (
						<div className="basicInfo_body_companyLogo_uploadInstruction">
							<Badge
								status="warning"
								text={`${t("imageupload_instruction1")}`}
							/>
							<Badge
								status="warning"
								text={`${t("imageupload_instruction2")}`}
							/>
							<Badge
								status="warning"
								text={`${t("imageupload_instruction3")}`}
							/>
						</div>
					)}
				</div>
				<div className="basicInfo_body_infoForm">
					{Object.entries(companyBasicDetails).length > 0 && (
						<Form
							form={form}
							layout="vertical"
							initialValues={
								companyBasicDetails && {
									name: companyBasicDetails.name,
									nameInEnglish: companyBasicDetails.nameInEnglish,
									industry: companyBasicDetails.industry,
									email: companyBasicDetails.email,
									address: companyBasicDetails.address,
									size: companyBasicDetails.size,
									website: companyBasicDetails.website,
								}
							}
							onFinish={updateInformation}
						>
							<Form.Item
								label={t("accountSetting_basicInfo_name")}
								name="name"
								rules={[{ required: true, message: "" }]}
							>
								<Input placeholder="" disabled={!isEditable} />
							</Form.Item>
							<Form.Item
								label={t("accountSetting_basicInfo_nameInEnglish")}
								name="nameInEnglish"
							>
								<Input placeholder="" disabled={!isEditable} />
							</Form.Item>
							<Form.Item
								label={t("accountSetting_basicInfo_industry")}
								name="industry"
							>
								<Input placeholder="" disabled={!isEditable} />
							</Form.Item>
							<Form.Item
								label={t("accountSetting_basicInfo_email")}
								name="email"
							>
								<Input disabled={!isEditable} />
							</Form.Item>
							<Form.Item
								label={t("accountSetting_basicInfo_address")}
								name="address"
							>
								<Input
									placeholder="Example: Tokyo, Japan .."
									disabled={!isEditable}
								/>
							</Form.Item>
							<Form.Item
								label={t("accountSetting_basicInfo_noOfEmployees")}
								name="size"
							>
								<Input
									placeholder="Company size/number of employees..."
									disabled={!isEditable}
								/>
							</Form.Item>
							<Form.Item
								label={t("accountSetting_basicInfo_website")}
								name="website"
								rules={[
									{ required: true, message: "Please enter company website!" },
								]}
							>
								<Input placeholder="Company website.." disabled={!isEditable} />
							</Form.Item>
						</Form>
					)}
				</div>
			</div>
		</Card>
	);
};
const mapStateToProps = (state) => ({
	companyBasicDetails: state.company.company,
	isActionProgress: state.company.actionInProgress,
});

export default connect(mapStateToProps, {
	updateCompanyBasicInformation,
	updateCompanyLogo,
})(BasicInformation);
